/**
 * Influencer referral capture.
 *
 * On any landing URL with `?ref=<code>`, we normalize the code, store it
 * immediately in localStorage (30-day TTL), then validate it in the background
 * via the `validate_referral_code` RPC (boolean existence check only — the
 * `referral_codes` table is not client-readable). If the code is
 * invalid/inactive, we remove it. Finally we strip the param from the URL.
 *
 * Storing first protects against fast remounts/reloads that could happen
 * between validation and persistence. The trigger / safety-net upsert will
 * silently no-op for codes that don't match an active row at signup time.
 *
 * The stored code is later attached to a signup:
 * - email/password: via `options.data.referral_code` → trigger writes profile
 * - OAuth: via safety-net upsert in `useAuth` SIGNED_IN handler
 */
import { supabase } from "@/integrations/supabase/client";
import { pushReferralEvent } from "@/lib/referralDebug";

const STORAGE_KEY = "grounded_referral_code";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_LEN = 40;

interface StoredReferral {
  code: string;
  ts: number;
}

export function normalizeReferralCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, MAX_LEN);
  return cleaned.length >= 2 ? cleaned : null;
}

export function getStoredReferralCode(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReferral;
    if (!parsed?.code || typeof parsed.ts !== "number") {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clearStoredReferralCode(): void {
  try {
    const before = localStorage.getItem(STORAGE_KEY);
    const stack = new Error("clear-stack").stack;
    console.log("[referral] clearStoredReferralCode call", {
      hadValue: !!before,
      before,
      stack,
    });
    pushReferralEvent("clearStoredReferralCode call", { hadValue: !!before, before, stack });
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function storeReferralCode(code: string): void {
  try {
    const payload: StoredReferral = { code, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    console.log("[referral] stored", { code, ts: payload.ts });
    pushReferralEvent("stored", { code, ts: payload.ts });
  } catch {
    // ignore
  }
}

function stripRefParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("ref")) return;
    url.searchParams.delete("ref");
    const newSearch = url.searchParams.toString();
    const newUrl = url.pathname + (newSearch ? `?${newSearch}` : "") + url.hash;
    window.history.replaceState(null, "", newUrl);
  } catch {
    // ignore
  }
}

/**
 * Read `?ref=` from the current URL, store immediately, validate in the
 * background, strip the URL. Idempotent and safe to call on every mount.
 */
export async function captureReferralFromUrl(): Promise<void> {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("ref");
  console.log("[referral] captureReferralFromUrl entry", {
    href: window.location.href,
    search: window.location.search,
    rawRef: raw,
  });
  pushReferralEvent("captureReferralFromUrl entry", {
    href: window.location.href,
    search: window.location.search,
    rawRef: raw,
  });
  const code = normalizeReferralCode(raw);
  console.log("[referral] normalized", { raw, normalized: code });
  pushReferralEvent("normalized", { raw, normalized: code });
  if (!code) return;

  // Store first so a fast remount/reload during validation doesn't lose it.
  storeReferralCode(code);

  try {
    // Existence-only check via SECURITY DEFINER RPC. Returns a boolean; the
    // referral_codes table itself is not readable by anon/authenticated.
    const { data, error } = await supabase.rpc("validate_referral_code", {
      _code: code,
    });
    const willClear = !error && data !== true;
    const result = {
      code,
      hasError: !!error,
      errorMsg: error?.message ?? null,
      dataFound: data === true,
      willClear,
    };
    console.log("[referral] validation result", result);
    pushReferralEvent("validation result", result);
    if (willClear) {
      console.log("[referral] cleared invalid", { code });
      pushReferralEvent("cleared invalid", { code });
      clearStoredReferralCode();
    }
  } catch (e) {
    console.log("[referral] validation threw", { error: String(e) });
    pushReferralEvent("validation threw", { error: String(e) });
  } finally {
    stripRefParam();
  }
}

